import Button from '@mui/material/Button/Button';
import React, { useState } from 'react';
import HomeIcon from '@mui/icons-material/Home';
import CodeIcon from '@mui/icons-material/Code';
import PollIcon from '@mui/icons-material/Poll';
import GavelIcon from '@mui/icons-material/Gavel';
import PeopleIcon from '@mui/icons-material/People';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import LowPriorityIcon from '@mui/icons-material/LowPriority';
import ArticleIcon from '@mui/icons-material/Article';

import Typography from '@mui/material/Typography/Typography';
import { Link } from 'react-router-dom';
import { QRCodeDialog } from '../QRContainer';
import Breadcrumbs from '@mui/material/Breadcrumbs/Breadcrumbs';
import Stack from '@mui/material/Stack/Stack';
import IconButton from '@mui/material/IconButton/IconButton';
import Menu from '@mui/material/Menu/Menu';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon/ListItemIcon';
import ListItemText from '@mui/material/ListItemText/ListItemText';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useDesktopMode } from '../../services/hooks/useDesktopMode';

const buttonStyle = {
    alignItems: 'center',
    display: 'flex',
    color: 'inherit'
}

export const HomeButton = () => {
    return (
        <Button component={Link} to="/" sx={buttonStyle}>
            <HomeIcon />
            <Typography>Home</Typography>
        </Button>
    )
}

export const HackathonButton = ({
    hackathonId,
    hackathonName
}: {
    hackathonId: string,
    hackathonName: string
}) => {
    return (
        <Button component={Link} to={`/h/${hackathonId}`} sx={buttonStyle}>
            <CodeIcon />
            <Typography>
                {hackathonName}
            </Typography>
        </Button>
    )
}

export enum PageType {
    Home,
    Hackathon,
    Idea,
    Organizer,
    Judge,
    HowTo,
    Article,
    Results,
    PresentationOrder
}

const iconMap = {
    [PageType.Home]: <HomeIcon />,
    [PageType.Hackathon]: <CodeIcon />,
    [PageType.Idea]: <LightbulbIcon />,
    [PageType.Organizer]: <PeopleIcon />,
    [PageType.Judge]: <GavelIcon />,
    [PageType.HowTo]: <HelpCenterIcon />,
    [PageType.Article]: <ArticleIcon />,
    [PageType.Results]: <PollIcon />,
    [PageType.PresentationOrder]: <LowPriorityIcon />
}

// a crumb name longer than this is cut off with an ellipsis (full name on hover / in the menu)
const MAX_CRUMB_WIDTH = 220;

/**
 * Breadcrumbs ported from Wayfinder. Desktop: back arrow + full trail. Phones, or trails deeper than
 * four levels: back arrow to the parent + the current page, with the ancestors in a dropdown - so long
 * hackathon and idea names never push the layout wider than the screen.
 */
export const AppBreadcrumbs = ({
    crumbs
}: {
    crumbs: Array<{
        type: PageType,
        name: string,
        link?: string,
    }>
}) => {
    const desktopMode = useDesktopMode();
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

    const parent = crumbs.length > 1 ? crumbs[crumbs.length - 2] : null;
    const backButton = parent && (
        parent.link ? (
            <IconButton component={Link} to={parent.link} size="small" aria-label={`Back to ${parent.name}`}>
                <ArrowBackIcon fontSize="small" />
            </IconButton>
        ) : (
            <IconButton size="small" disabled aria-label={parent.name}>
                <ArrowBackIcon fontSize="small" />
            </IconButton>
        )
    );

    if ((!desktopMode || crumbs.length > 4) && crumbs.length > 1) {
        const current = crumbs[crumbs.length - 1];
        const ancestors = crumbs.slice(0, -1).reverse();
        return (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ py: 1, minWidth: 0 }}>
                {backButton}
                <Button
                    size="small"
                    color="inherit"
                    endIcon={<ExpandMoreIcon fontSize="small" />}
                    onClick={(e) => setMenuAnchor(e.currentTarget)}
                    aria-haspopup="menu"
                    aria-label={`${current.name}, show parent pages`}
                    sx={{ textTransform: 'none', fontWeight: 500, minWidth: 0, px: 0.5 }}
                >
                    <Typography variant="body2" noWrap sx={{ fontWeight: 500, maxWidth: 180 }}>
                        {current.name}
                    </Typography>
                </Button>
                <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    slotProps={{ paper: { sx: { maxWidth: 'calc(100vw - 32px)' } } }}
                >
                    {ancestors.map((crumb, index) => (
                        <MenuItem
                            key={index}
                            component={crumb.link ? Link : 'li'}
                            to={crumb.link ?? ''}
                            onClick={() => setMenuAnchor(null)}
                            disabled={!crumb.link}
                        >
                            <ListItemIcon sx={{ minWidth: 32 }}>{iconMap[crumb.type]}</ListItemIcon>
                            <ListItemText primary={crumb.name} primaryTypographyProps={{ noWrap: true }} />
                        </MenuItem>
                    ))}
                </Menu>
                <QRCodeDialog />
            </Stack>
        );
    }

    return (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
            {backButton}
            <Breadcrumbs sx={{ py: 1, minWidth: 0 }}>
                {
                    crumbs.map((crumb, index) => {
                        const label = (
                            <Typography noWrap title={crumb.name} sx={{ maxWidth: MAX_CRUMB_WIDTH }}>{crumb.name}</Typography>
                        );
                        if (crumb.link) {
                            return (
                                <Button key={index} component={Link} to={crumb.link} sx={{ ...buttonStyle, minWidth: 0, gap: 0.5 }}>
                                    {iconMap[crumb.type]}
                                    {label}
                                </Button>
                            )
                        }

                        return (
                            <Stack key={index} direction="row" alignItems="center" spacing={0.5} sx={{ ...buttonStyle, minWidth: 0 }}>
                                {iconMap[crumb.type]}
                                {label}
                                {
                                    index === crumbs.length - 1 && <QRCodeDialog />
                                }
                            </Stack>
                        )
                    })
                }
            </Breadcrumbs>
        </Stack>
    )
}
